/**
 * Unilar Categorias Circulares
 * Infinite carousel on all breakpoints via FancyApps Carousel.
 */

if (!customElements.get('categorias-circulares-slider')) {
  class CategoriasCircularesSlider extends HTMLElement {
    /** @type {import('fancyapps').Carousel | null} */
    #carousel = null;
    /** @type {HTMLElement | null} */
    #cartDrawer = null;
    /** @type {(() => void) | null} */
    #drawerOpenHandler = null;
    /** @type {number | null} */
    #carouselWaitFrame = null;
    #hasActivated = false;
    /** @type {Set<HTMLElement>} */
    #inertSlides = new Set();

    #syncSlideInert = () => {
      if (!this.#carousel || typeof this.#carousel.getVisibleSlides !== 'function') return;

      const visibleSlides = this.#carousel.getVisibleSlides(0);

      this.#carousel.slides.forEach((slide) => {
        const element = slide.el;
        if (!(element instanceof HTMLElement)) return;

        if (visibleSlides.has(slide)) {
          element.removeAttribute('inert');
          element.setAttribute('aria-hidden', 'false');
          this.#inertSlides.delete(element);
        } else {
          element.setAttribute('inert', '');
          element.setAttribute('aria-hidden', 'true');
          this.#inertSlides.add(element);
        }
      });
    };

    connectedCallback() {
      if (this.dataset.initOnDrawerOpen !== 'true' || window.Shopify?.designMode === true) {
        this.#activate();
        return;
      }

      const cartDrawer = this.closest('cart-drawer-component');

      if (!(cartDrawer instanceof HTMLElement)) {
        this.#activate();
        return;
      }

      const dialog = cartDrawer.querySelector('dialog[ref="dialog"]');

      if (!(dialog instanceof HTMLDialogElement) || dialog.open) {
        this.#activate();
        return;
      }

      this.#cartDrawer = cartDrawer;
      this.#drawerOpenHandler = () => this.#activate();
      cartDrawer.addEventListener('dialog:open', this.#drawerOpenHandler, { once: true });
    }

    disconnectedCallback() {
      this.#removeDrawerOpenListener();
      this.#cancelCarouselWait();
      this.#destroy();
      this.#hasActivated = false;
    }

    #activate() {
      if (this.#hasActivated || !this.isConnected) return;

      this.#hasActivated = true;
      this.#removeDrawerOpenListener();
      this.#init();
    }

    #removeDrawerOpenListener() {
      if (this.#cartDrawer && this.#drawerOpenHandler) {
        this.#cartDrawer.removeEventListener('dialog:open', this.#drawerOpenHandler);
      }

      this.#cartDrawer = null;
      this.#drawerOpenHandler = null;
    }

    #cancelCarouselWait() {
      if (this.#carouselWaitFrame === null) return;

      cancelAnimationFrame(this.#carouselWaitFrame);
      this.#carouselWaitFrame = null;
    }

    #destroy() {
      if (this.#carousel) {
        this.#carousel.off('ready change settle refresh', this.#syncSlideInert);
        this.#clearSlideInert();
        this.#carousel.destroy();
        this.#carousel = null;
      }

      this.classList.remove('is-carousel-active');
    }

    #clearSlideInert() {
      this.#inertSlides.forEach((slide) => slide.removeAttribute('inert'));
      this.#inertSlides.clear();
    }

    #init() {
      const start = () => {
        this.#carouselWaitFrame = null;

        if (!this.isConnected || !this.#hasActivated || this.#carousel) return;

        if (!window.Carousel) {
          this.#carouselWaitFrame = requestAnimationFrame(start);
          return;
        }

        this.#carousel = new window.Carousel(this, {
          Dots: false,
          Navigation: this.dataset.navigation === 'true',
          infinite: true,
          center: false,
          fill: false,
          dragFree: true,
          slidesPerPage: 1,
        });

        this.#carousel.on('ready change settle refresh', this.#syncSlideInert);
        this.classList.add('is-carousel-active');
      };

      this.#carouselWaitFrame = requestAnimationFrame(start);
    }
  }

  customElements.define('categorias-circulares-slider', CategoriasCircularesSlider);
}
